// One page for all six repeating sections.
//
// The Rails app had a view, a partial and a save action per section, with control flow keyed
// off the submit button's caption. Here the section is described once and the page is built
// from that description, so adding a field is a one-line change in one place.

import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type SectionRow } from '../api.ts';
import {
  ErrorNotice, Loading, Field, TextInput, TextArea, StateSelect,
} from '../components/Bits.tsx';

interface FieldDef {
  name: string;
  label: string;
  type?: 'text' | 'number' | 'textarea' | 'state';
  hint?: string;
  required?: boolean;
  /** Width hint so short fields sit side by side. */
  half?: boolean;
}

interface SectionDef {
  path: string;
  title: string;
  blurb: string;
  /** How one entry is titled in the list. */
  summary: (row: SectionRow) => string;
  fields: FieldDef[];
}

const DATE_HINT = 'Any format is fine — "September 2024", "9/2024", "Fall 2024".';

export const SECTION_DEFS: Record<string, SectionDef> = {
  schools: {
    path: 'schools',
    title: 'Education',
    blurb: 'List the high school you attend or attended. Add more than one if you transferred.',
    summary: (r) => String(r.school ?? 'School'),
    fields: [
      { name: 'school', label: 'High school', required: true },
      { name: 'city', label: 'City', required: true, half: true },
      { name: 'state', label: 'State', type: 'state', required: true, half: true },
      { name: 'date_started', label: 'Date started', required: true, half: true, hint: DATE_HINT },
      { name: 'date_ended', label: 'Date ended', required: true, half: true, hint: DATE_HINT },
      { name: 'class_rank', label: 'Class rank', type: 'number', half: true, hint: 'Leave blank if your school does not rank.' },
      { name: 'class_size', label: 'Class size', type: 'number', half: true },
      { name: 'gpa', label: 'GPA', required: true, hint: 'However your school reports it — "3.8", "3.8/4.0", or a percentage.' },
      // The scale is not asked: every current applicant sits the 1600-point SAT, so the
      // server fills it in. Only historical records are ambiguous.
      { name: 'sat', label: 'SAT score', type: 'number', half: true, hint: 'Out of 1600. Leave blank if you took the ACT.' },
      { name: 'act', label: 'ACT score', type: 'number', half: true, hint: 'Out of 36. Leave blank if you took the SAT.' },
    ],
  },
  jobs: {
    path: 'jobs',
    title: 'Work experience',
    blurb: 'Paid work you have done, including part-time and summer jobs.',
    summary: (r) => [r.title, r.employer].filter(Boolean).join(' — ') || 'Job',
    fields: [
      { name: 'employer', label: 'Employer', required: true },
      { name: 'title', label: 'Job title', required: true },
      { name: 'description', label: 'What did you do?', type: 'textarea' },
      { name: 'date_started', label: 'Date started', half: true, hint: DATE_HINT },
      { name: 'date_ended', label: 'Date ended', half: true, hint: 'Leave blank if you still work there.' },
      { name: 'hours_per_week', label: 'Hours per week', type: 'number', half: true },
    ],
  },
  community: {
    path: 'community',
    title: 'Community service',
    blurb: 'Volunteering and service to your community.',
    summary: (r) => String(r.organization ?? 'Activity'),
    fields: [
      { name: 'organization', label: 'Organization', required: true },
      { name: 'description', label: 'What did you do?', type: 'textarea' },
      { name: 'date_started', label: 'Date started', half: true, hint: DATE_HINT },
      { name: 'date_ended', label: 'Date ended', half: true },
      { name: 'hours', label: 'Total hours', type: 'number', half: true },
      { name: 'role', label: 'Your role', half: true, hint: 'For example: volunteer, organiser, team lead.' },
    ],
  },
  extracurricular: {
    path: 'extracurricular',
    title: 'Extracurricular activities',
    blurb: 'Clubs, sports, music, and anything else you take part in outside class.',
    summary: (r) => [r.activity, r.organization].filter(Boolean).join(' — ') || 'Activity',
    fields: [
      { name: 'organization', label: 'Organization or school', required: true },
      { name: 'activity', label: 'Activity' },
      { name: 'date_started', label: 'Date started', half: true, hint: DATE_HINT },
      { name: 'date_ended', label: 'Date ended', half: true },
      { name: 'hours_per_week', label: 'Hours per week', type: 'number', half: true },
      { name: 'role', label: 'Your role', half: true },
    ],
  },
  awards: {
    path: 'awards',
    title: 'Awards and honors',
    blurb: 'Recognition you have received, at school or outside it.',
    summary: (r) => String(r.name ?? 'Award'),
    fields: [
      { name: 'name', label: 'Award', required: true },
      { name: 'organization', label: 'Awarded by' },
      { name: 'date_received', label: 'Date received', half: true, hint: DATE_HINT },
      { name: 'description', label: 'What was it for?', type: 'textarea' },
    ],
  },
  recommendations: {
    path: 'recommendations',
    title: 'References',
    blurb:
      'Two people who can speak to your character and your work. They will not be contacted unless your application advances.',
    summary: (r) => [r.prefix, r.first_name, r.last_name].filter(Boolean).join(' ') || 'Reference',
    fields: [
      { name: 'prefix', label: 'Title', half: true, hint: 'Mr., Ms., Dr., and so on.' },
      { name: 'first_name', label: 'First name', required: true, half: true },
      { name: 'last_name', label: 'Last name', required: true, half: true },
      { name: 'daytime_phone', label: 'Daytime phone', half: true },
      { name: 'relationship', label: 'How do they know you?', hint: 'For example: teacher, coach, employer.' },
    ],
  },
};

type FormValues = Record<string, string>;

const emptyForm = (def: SectionDef): FormValues =>
  Object.fromEntries(def.fields.map((f) => [f.name, '']));

const rowToForm = (def: SectionDef, row: SectionRow): FormValues =>
  Object.fromEntries(def.fields.map((f) => [f.name, row[f.name] == null ? '' : String(row[f.name])]));

export function SectionPage() {
  const { section = '' } = useParams();
  const def = SECTION_DEFS[section];
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const [form, setForm] = useState<FormValues>({});

  const { data, isPending, error } = useQuery({
    queryKey: ['section', section],
    queryFn: () => api.get<{ rows: SectionRow[] }>(`/api/application/${section}`),
    enabled: Boolean(def),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['section', section] });
    // The dashboard checklist depends on whether this section has any rows.
    queryClient.invalidateQueries({ queryKey: ['application'] });
  };

  const save = useMutation({
    mutationFn: (payload: { id: number | 'new'; values: FormValues }) =>
      payload.id === 'new'
        ? api.post(`/api/application/${section}`, payload.values)
        : api.put(`/api/application/${section}/${payload.id}`, payload.values),
    onSuccess: () => { invalidate(); setEditing(null); },
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/api/application/${section}/${id}`),
    onSuccess: invalidate,
  });

  if (!def) return <main id="main"><h2>Unknown section</h2><Link to="/apply">Back</Link></main>;
  if (isPending) return <main id="main"><Loading /></main>;
  if (error) return <main id="main"><ErrorNotice error={error} /></main>;

  const rows = data?.rows ?? [];

  const startNew = () => { setForm(emptyForm(def)); setEditing('new'); };
  const startEdit = (row: SectionRow) => { setForm(rowToForm(def, row)); setEditing(row.id); };

  const renderField = (field: FieldDef) => (
    <div key={field.name} style={{ flex: field.half ? '1 1 12rem' : '1 1 100%' }}>
      <Field label={field.label + (field.required ? ' *' : '')} hint={field.hint}>
        {field.type === 'state' ? (
          <StateSelect
            value={form[field.name] ?? ''}
            required={field.required}
            onChange={(e) => setForm({ ...form, [field.name]: e.target.value })}
          />
        ) : field.type === 'textarea' ? (
          <TextArea
            value={form[field.name] ?? ''}
            required={field.required}
            onChange={(e) => setForm({ ...form, [field.name]: e.target.value })}
          />
        ) : (
          <TextInput
            type={field.type === 'number' ? 'number' : 'text'}
            value={form[field.name] ?? ''}
            required={field.required}
            min={field.type === 'number' ? 0 : undefined}
            onChange={(e) => setForm({ ...form, [field.name]: e.target.value })}
          />
        )}
      </Field>
    </div>
  );

  return (
    <main id="main">
      <p><Link to="/apply">← Back to your application</Link></p>
      <h2>{def.title}</h2>
      <p className="lede">{def.blurb}</p>

      <ErrorNotice error={save.error ?? remove.error} />

      {rows.length === 0 && editing === null && (
        <p className="empty">Nothing added yet.</p>
      )}

      {rows.map((row) => (
        editing === row.id ? (
          <form
            key={row.id}
            className="panel"
            onSubmit={(e) => { e.preventDefault(); save.mutate({ id: row.id, values: form }); }}
          >
            <div className="row">{def.fields.map(renderField)}</div>
            <div className="actions">
              <button type="submit" disabled={save.isPending}>Save</button>
              <button type="button" className="secondary" onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </form>
        ) : (
          <div className="entry" key={row.id}>
            <header>
              <h4>{def.summary(row)}</h4>
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <button className="quiet" onClick={() => startEdit(row)}>Edit</button>
                <button
                  className="quiet"
                  onClick={() => {
                    if (window.confirm('Remove this entry?')) remove.mutate(row.id);
                  }}
                >
                  Remove
                </button>
              </div>
            </header>
            <dl className="dl">
              {def.fields.map((f) => {
                const value = row[f.name];
                if (value == null || value === '') return null;
                return (
                  <div key={f.name} style={{ display: 'contents' }}>
                    <dt>{f.label}</dt>
                    <dd>{String(value)}</dd>
                  </div>
                );
              })}
            </dl>
          </div>
        )
      ))}

      {editing === 'new' && (
        <form
          className="panel"
          onSubmit={(e) => { e.preventDefault(); save.mutate({ id: 'new', values: form }); }}
        >
          <h3 style={{ marginTop: 0 }}>New entry</h3>
          <div className="row">{def.fields.map(renderField)}</div>
          <div className="actions">
            <button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="secondary" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </form>
      )}

      {editing === null && (
        <div className="actions">
          <button onClick={startNew}>Add {rows.length ? 'another' : 'an entry'}</button>
          <button className="secondary" onClick={() => navigate('/apply')}>Done</button>
        </div>
      )}
    </main>
  );
}
